import { fetchJson } from '../lib/api';
import { apiBase } from '../lib/apiBase';

export async function apiFetch(path, opts) {
  const res = await fetchJson(path, opts);

  if (res.unauth) return { ok: false, unauth: true };

  // Treat legacy 404s as a benign "no data yet" response so paper-mode
  // dashboards don't spam the console with errors.
  if (res.status === 404 || res.status === 'no_data_yet') {
    const payload = res.data || {};
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object' && Array.isArray(payload.data)) return payload.data;
    if (payload && typeof payload === 'object' && Object.keys(payload).length === 0) {
      return { message: 'no_data_yet' };
    }
    return payload || { message: 'no_data_yet' };
  }

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  return res.data ?? { ok: true, message: 'no_data_yet' };
}

export const publicJson = apiFetch;

export const safeArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && Array.isArray(v.data)) return v.data;
  return [];
};
export const safeObj = (v) => (v && typeof v === 'object' ? v : {});

export async function fetchAiReviewStatus() {
  return fetchJson(apiBase('ai_review_status'));
}

export async function postAiReviewAction(body = {}) {
  const payload = body || {};
  return fetchJson(apiBase('ai_review_action'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function fetchTradeHistory(limit = 200) {
  const query = limit ? `?limit=${limit}` : '';
  return fetchJson(apiBase(`trade_history${query}`));
}

export async function fetchTradeStats(limit = 200) {
  const query = limit ? `?limit=${limit}` : '';
  return fetchJson(apiBase(`trade_stats${query}`));
}


export async function fetchMcpReviewStatus() {
  return fetchJson(apiBase('mcp_review_status'));
}

export async function fetchMcpReviewRecommendations({ limit = 20, status, severity, category, includeTerminal = false, includeRoutineInfo = false } = {}) {
  const normalizedStatus = status == null ? '' : String(status).trim();
  const normalizedSeverity = severity == null ? '' : String(severity).trim();
  const normalizedCategory = category == null ? '' : String(category).trim();
  const qs = new URLSearchParams();
  qs.set('limit', String(Math.min(Math.max(Number(limit) || 20, 1), 50)));
  qs.set('include_terminal', includeTerminal || normalizedStatus ? 'true' : 'false');
  qs.set('include_routine_info', includeRoutineInfo ? 'true' : 'false');
  if (normalizedStatus) qs.set('status', normalizedStatus);
  if (normalizedSeverity) qs.set('severity', normalizedSeverity);
  if (normalizedCategory) qs.set('category', normalizedCategory);
  return fetchJson(`${apiBase('mcp_review_recommendations')}?${qs.toString()}`);
}

export async function fetchMcpReviewQueue({ limit = 10 } = {}) {
  const bounded = Math.min(Math.max(Number(limit) || 10, 1), 50);
  return fetchJson(`${apiBase('mcp_review_queue')}?limit=${bounded}`);
}

function postMcpRecommendationStatus(id, action, operatorNote) {
  const payload = {};
  if (operatorNote) payload.operator_note = String(operatorNote).slice(0, 500);
  return fetchJson(apiBase(`mcp_review_recommendations/${encodeURIComponent(id)}/${action}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function approveMcpRecommendation(id, operatorNote) {
  return postMcpRecommendationStatus(id, 'approve', operatorNote);
}

export async function rejectMcpRecommendation(id, operatorNote) {
  return postMcpRecommendationStatus(id, 'reject', operatorNote);
}

export async function supersedeMcpRecommendation(id, operatorNote) {
  return postMcpRecommendationStatus(id, 'supersede', operatorNote);
}


export async function previewMcpRecommendationApply(id, actionId) {
  const payload = {};
  if (actionId) payload.action_id = String(actionId).slice(0, 128);
  return fetchJson(apiBase(`mcp_review_recommendations/${encodeURIComponent(id)}/apply_preview`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function applyMcpRecommendation(id, { action_id, pin, operator_note } = {}) {
  const payload = {
    pin: String(pin || '').slice(0, 16),
  };
  if (action_id) payload.action_id = String(action_id).slice(0, 128);
  if (operator_note) payload.operator_note = String(operator_note).slice(0, 300);
  return fetchJson(apiBase(`mcp_review_recommendations/${encodeURIComponent(id)}/apply`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
